import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltDropdown, CobaltDropdownItem } from '@cae/cobalt-react/components/dropdown';
import {
  CobaltFormField,
  CobaltFormFieldHelperText,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { EditIdentityPanelProps } from './EditIdentityPanel.types';

import { useEditIdentityPanel } from '../../hooks/use-edit-identity-panel/useEditIdentityPanel';
import { EditIdentityActionBar } from './edit-identity-action-bar/EditIdentityActionBar';
import { EditIdentityBirthdateField } from './edit-identity-birthdate-field/EditIdentityBirthdateField';
import styles from './EditIdentityPanel.module.css';
import { CONFIRM_MODAL_BODY, PANEL_DESCRIPTION, SALUTATION_OPTIONS } from './EditIdentityPanel.utils';

const EditIdentityPanel = React.memo(
  ({ onClose }: Readonly<EditIdentityPanelProps>): React.ReactElement => {
    const {
      formState,
      errors,
      isMobile,
      isConfirmOpen,
      isExitModalOpen,
      isDirty,
      isPending,
      isError,
      isProfileLoading,
      isProfileReady,
      handleFieldChange,
      handleDateSelect,
      handleDateInputChange,
      handleSubmit,
      handleConfirm,
      handleCancelConfirm,
      handleCloseAttempt,
      handleExit,
      handleKeepEditing,
    } = useEditIdentityPanel({ onClose });

    const isDisabled = isPending || (isProfileLoading && !isProfileReady);

    return (
      <>
        <CobaltSidePanel
          isOpen
          showCloseButton
          surtitle="Personal information"
          title="Identity information"
          description={PANEL_DESCRIPTION}
          onClose={handleCloseAttempt}
          actionBar={
            <EditIdentityActionBar
              isMobile={isMobile}
              isPending={isPending}
              isProfileReady={isProfileReady}
              isDirty={isDirty}
              onCloseAttempt={handleCloseAttempt}
              onSubmit={handleSubmit}
            />
          }
          data-heap-id="edit-identity-panel"
          size={isMobile ? 'large' : 'small'}
        >
          <div className={styles.form}>
            {isDisabled && <ContentLoader />}
            {isError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>
                    We couldn't save your information. Please try again.
                  </h3>
                }
                data-heap-id="mutation-error-banner"
              />
            )}
            <CobaltFormField id="salutation">
              <CobaltFormFieldLabel
                htmlFor="salutation"
                label="Salutation (optional)"
              />
              <CobaltDropdown
                id="salutation"
                placeholder="Select salutation"
                value={formState.salutation || undefined}
                onSelectionChange={value =>
                  handleFieldChange('salutation', (value as string) ?? '')
                }
                clearSelection={true}
                disabled={isDisabled}
              >
                {SALUTATION_OPTIONS.map(option => (
                  <CobaltDropdownItem key={option} value={option}>
                    {option}
                  </CobaltDropdownItem>
                ))}
              </CobaltDropdown>
            </CobaltFormField>
            <CobaltFormField id="givenName" error={errors.givenName}>
              <CobaltFormFieldLabel htmlFor="givenName" label="First name" />
              <CobaltTextInput
                id="givenName"
                value={formState.givenName}
                onChange={e => handleFieldChange('givenName', e.target.value)}
                disabled={isDisabled}
              />
              {errors.givenName && (
                <CobaltFormFieldHelperText helperId="givenName-error">
                  {errors.givenName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>
            <CobaltFormField id="middleName" error={errors.middleName}>
              <CobaltFormFieldLabel
                htmlFor="middleName"
                label="Middle name (if applicable)"
              />
              <CobaltTextInput
                id="middleName"
                value={formState.middleName}
                onChange={e =>
                  handleFieldChange('middleName', e.target.value)
                }
                disabled={isDisabled}
              />
              {errors.middleName && (
                <CobaltFormFieldHelperText helperId="middleName-error">
                  {errors.middleName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>
            <CobaltFormField id="lastName" error={errors.lastName}>
              <CobaltFormFieldLabel htmlFor="lastName" label="Last name" />
              <CobaltTextInput
                id="lastName"
                value={formState.lastName}
                onChange={e => handleFieldChange('lastName', e.target.value)}
                disabled={isDisabled}
              />
              {errors.lastName && (
                <CobaltFormFieldHelperText helperId="lastName-error">
                  {errors.lastName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>
            <EditIdentityBirthdateField
              value={formState.birthdate}
              error={errors.birthdate}
              disabled={isDisabled}
              onDateSelect={handleDateSelect}
              onChange={handleDateInputChange}
            />
          </div>
        </CobaltSidePanel>

        <CobaltModal
          isOpen={isConfirmOpen}
          title="Confirm change"
          showCloseButton={false}
          onClose={handleCancelConfirm}
          actionBar={
            <CobaltActionBar
              position="right"
              mobile={isMobile}
              primaryButton={
                <CobaltButton
                  data-heap-id="profile-identity-confirm-btn"
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={isPending}
                >
                  Confirm
                </CobaltButton>
              }
              secondaryButton={
                <CobaltButton
                  data-heap-id="profile-identity-cancel-confirm-btn"
                  variant="secondary"
                  onClick={handleCancelConfirm}
                  disabled={isPending}
                >
                  Cancel
                </CobaltButton>
              }
            />
          }
        >
          <p className={styles.modalBody}>{CONFIRM_MODAL_BODY}</p>
        </CobaltModal>

        <UnsavedChangesModal
          isOpen={isExitModalOpen}
          confirmLabel="Keep Editing"
          cancelLabel="Exit"
          onConfirm={handleKeepEditing}
          onCancel={handleExit}
        />
      </>
    );
  }
);

EditIdentityPanel.displayName = 'EditIdentityPanel';

export { EditIdentityPanel };
